// Markdown editor with Write/Preview tabs. Stored value is markdown.
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Bold, Italic, Heading2, List, Link as LinkIcon, Eye, Edit3 } from "lucide-react";

const PROSE_CLS = "prose prose-stone max-w-none prose-headings:font-display prose-headings:font-normal prose-p:leading-relaxed prose-p:text-[var(--ff-text-secondary)] prose-strong:text-[var(--ff-text-primary)] prose-a:text-[var(--ff-primary)] prose-a:no-underline hover:prose-a:underline";

export function MarkdownPreview({ value, className = "" }) {
  if (!value) return <p className="text-[var(--ff-text-muted)] italic text-sm">Nothing to preview yet.</p>;
  return (
    <div className={`${PROSE_CLS} ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
        a: (p) => <a {...p} target="_blank" rel="noopener noreferrer"/>,
      }}>{value}</ReactMarkdown>
    </div>
  );
}

function wrapSelection(textarea, before, after = before) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selected = textarea.value.slice(start, end);
  const replacement = before + selected + after;
  textarea.setRangeText(replacement, start, end, "end");
  textarea.focus();
  // Fire input so React state updates
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
}

function prefixLines(textarea, prefix) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const before = textarea.value.slice(0, start);
  const selected = textarea.value.slice(start, end) || "";
  const after = textarea.value.slice(end);
  const newSelected = selected
    .split("\n")
    .map((line) => (line ? prefix + line : line))
    .join("\n") || prefix;
  textarea.value = before + newSelected + after;
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
  textarea.focus();
}

export default function MarkdownEditor({ value, onChange, rows = 8, placeholder = "Write a story…", testId = "md-editor" }) {
  const [mode, setMode] = useState("write");
  let taRef = null;

  const cmd = (fn) => () => { if (taRef) fn(taRef); };

  return (
    <div className="border border-[var(--ff-border)] rounded-lg overflow-hidden bg-white" data-testid={testId}>
      <div className="flex items-center justify-between border-b border-[var(--ff-border)] bg-[var(--ff-muted)] px-2 py-1.5">
        <div className="flex items-center gap-1 text-[var(--ff-text-secondary)]">
          <button type="button" onClick={cmd(t => wrapSelection(t, "**"))} className="p-1.5 hover:bg-white rounded" title="Bold" data-testid={`${testId}-bold`}><Bold size={14}/></button>
          <button type="button" onClick={cmd(t => wrapSelection(t, "*"))} className="p-1.5 hover:bg-white rounded" title="Italic" data-testid={`${testId}-italic`}><Italic size={14}/></button>
          <button type="button" onClick={cmd(t => prefixLines(t, "## "))} className="p-1.5 hover:bg-white rounded" title="Heading" data-testid={`${testId}-h2`}><Heading2 size={14}/></button>
          <button type="button" onClick={cmd(t => prefixLines(t, "- "))} className="p-1.5 hover:bg-white rounded" title="List" data-testid={`${testId}-list`}><List size={14}/></button>
          <button type="button" onClick={cmd(t => wrapSelection(t, "[", "](https://)"))} className="p-1.5 hover:bg-white rounded" title="Link" data-testid={`${testId}-link`}><LinkIcon size={14}/></button>
        </div>
        <div className="flex items-center text-xs">
          <button type="button" onClick={() => setMode("write")} className={`px-2.5 py-1 rounded-l ${mode === "write" ? "bg-white text-[var(--ff-text-primary)] shadow-sm" : "text-[var(--ff-text-muted)]"}`} data-testid={`${testId}-write`}>
            <Edit3 size={12} className="inline mr-1"/> Write
          </button>
          <button type="button" onClick={() => setMode("preview")} className={`px-2.5 py-1 rounded-r ${mode === "preview" ? "bg-white text-[var(--ff-text-primary)] shadow-sm" : "text-[var(--ff-text-muted)]"}`} data-testid={`${testId}-preview`}>
            <Eye size={12} className="inline mr-1"/> Preview
          </button>
        </div>
      </div>
      {mode === "write" ? (
        <textarea
          ref={(el) => { taRef = el; }}
          rows={rows}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full p-3 outline-none font-mono text-sm leading-relaxed resize-y"
          data-testid={`${testId}-textarea`}
        />
      ) : (
        <div className="p-4 min-h-[200px] bg-white" data-testid={`${testId}-preview-pane`}>
          <MarkdownPreview value={value}/>
        </div>
      )}
      <p className="text-[11px] text-[var(--ff-text-muted)] px-3 py-2 border-t border-[var(--ff-border)] bg-[var(--ff-muted)]">
        Markdown supported: **bold**, *italic*, ## heading, - list, [link](url). Line breaks are preserved.
      </p>
    </div>
  );
}
